import { useState } from 'react';
import LessonFormModal from './LessonFormModal';
import { addLectureApi, updateLessonQuizApi } from '../../../../api/apiServices';
import QuizEditor from './QuizEditor';

export default function LessonManager({ 
  courseId,
  sectionId,
  lessons = [], 
  onChange 
}) {
  const [editingLesson, setEditingLesson] = useState(null);
  const [isNewLesson, setIsNewLesson] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [quizLesson, setQuizLesson] = useState(null);

  // Local notification state
  const [notification, setNotification] = useState({ 
    show: false, 
    type: '', 
    message: '' 
  });

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 4000);
  };

  // Real API call with sectionId
  const handleSaveLesson = async (lessonData, slideFiles = {}) => {
    if (!sectionId) {
      showNotification('error', "Section ID is missing. Please try again.");
      return;
    }

    setActionLoading(true);

    try {
      const result = await addLectureApi(courseId, sectionId, lessonData, slideFiles);

      if (result.success) {
        const savedLesson = {
          ...lessonData,
          id: result.lecture?._id || `lesson-${Date.now()}`,
        };

        if (isNewLesson) {
          onChange([...lessons, savedLesson]);
        } else {
          onChange(lessons.map(l => l.id === lessonData.id ? savedLesson : l));
        }

        showNotification('success', 'Lesson saved successfully!');
      } else {
        showNotification('error', result.message || "Failed to save lesson");
      }
    } catch (err) {
      console.error("Failed to save lesson:", err);
      showNotification('error', err.response?.data?.message || "Failed to save lesson. Please try again.");
    } finally {
      setActionLoading(false);
      setEditingLesson(null);
      setIsNewLesson(false);
    }
  };

  const handleDeleteLesson = (lessonId) => {
    onChange(lessons.filter(l => l.id !== lessonId));
  };

  const handleNewLesson = () => {
    const newLesson = {
      id: `lesson-${Date.now()}`,
      title: '',
      type: 'video',
      videoUrl: '',
      textContent: '',
      slides: [],
      duration: '',
      isPreview: false,
      order: lessons.length + 1,
    };
    setEditingLesson(newLesson);
    setIsNewLesson(true);
  };

   // Save Lesson Quiz


  console.log("Quiz Lesson from LessonManager: ", quizLesson)

  const handleSaveLessonQuiz = async (quizData) => {
    if (!quizLesson || !sectionId) {
      showNotification('error', "Cannot save quiz - missing data");
      return;
    }

    // CRITICAL: Use the real MongoDB _id, not the temporary frontend id
  const lectureId = quizLesson._id || quizLesson.id;

  if (!lectureId) {
    showNotification('error', "Lecture ID is missing. Please refresh and try again.");
    return;
  }

  console.log("Saving quiz for lecture ID:", lectureId);

    setActionLoading(true);

    try {
      const result = await updateLessonQuizApi(
        courseId, 
        sectionId, 
        lectureId, 
        {
          title: quizData.title || "Lesson Quiz",
          questions: (quizData.questions || []).map(q => ({
            questionText: q.text?.trim() || q.questionText?.trim() || '',
            options: q.options || [],
            correctAnswer: Number(q.correctAnswer) || 0,
            explanation: q.explanation?.trim() || ''
          })).filter(q => q.questionText),
          passingScore: quizData.passingScore || 70
        }
      );

      if (result.success) {
        const updatedLesson = { 
          ...quizLesson, 
          quiz: result.quiz 
        };

        onChange(lessons.map(l => l.id === quizLesson.id ? updatedLesson : l));
        setQuizLesson(null);
        showNotification('success', 'Lesson quiz saved successfully!');
      }
    } catch (err) {
      console.error("Failed to save lesson quiz:", err);
      showNotification('error', err.response?.data?.message || "Failed to save lesson quiz");
    } finally {
      setActionLoading(false);
    }
  };

  const sortedLessons = [...lessons].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="max-w-3xl">
      {/* Notification */}
      {notification.show && (
        <div className={`mb-4 px-4 py-3 rounded-2xl border flex items-center gap-3 text-sm font-medium ${
          notification.type === 'success' 
            ? 'bg-emerald-50 border-emerald-200 text-emerald-700' 
            : 'bg-rose-50 border-rose-200 text-rose-700'
        }`}>
          <i className={`ri-${notification.type === 'success' ? 'check-circle' : 'error-warning'}-line text-lg`} />
          {notification.message}
        </div>
      )}

      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-base font-bold text-slate-900">Lessons</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {lessons.length} lesson{lessons.length !== 1 ? 's' : ''} added
          </p>
        </div>
        <button
          onClick={handleNewLesson}
          disabled={actionLoading}
          className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 disabled:opacity-70 cursor-pointer transition-colors whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></div>
          Add Lesson
        </button>
      </div>

      {sortedLessons.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-3xl p-16 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-slate-100 rounded-2xl flex items-center justify-center">
            <i className="ri-play-list-add-line text-4xl text-slate-400" />
          </div>
          <p className="text-slate-700 font-semibold text-lg">No lessons yet</p>
          <p className="text-slate-500 mt-2 mb-6">Start building your course by adding lessons</p>
          <button
            onClick={handleNewLesson}
            className="px-6 py-3 bg-teal-600 text-white font-semibold rounded-2xl hover:bg-teal-700 transition-colors"
          >
            Add Your First Lesson
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {sortedLessons.map((lesson, idx) => (
            <div
              key={lesson._id || lesson.id}
              className="bg-white rounded-xl border border-slate-100 p-4 flex items-center gap-4 hover:border-slate-200 transition-colors"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-slate-100 text-slate-500 font-mono text-sm rounded-xl flex-shrink-0">
                {idx + 1}
              </div>

              <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${
                lesson.type === 'video' ? 'bg-red-100 text-red-600' :
                lesson.type === 'slides' ? 'bg-amber-100 text-amber-600' :
                'bg-blue-100 text-blue-600'
              }`}>
                <i className={
                  lesson.type === 'video' ? 'ri-play-circle-fill text-xl' :
                  lesson.type === 'slides' ? 'ri-slideshow-3-line text-xl' :
                  'ri-article-line text-xl'
                } />
              </div>

              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 line-clamp-1">{lesson.title || 'Untitled Lesson'}</p>
                <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                  <span className="capitalize font-medium">{lesson.type}</span>
                  {lesson.duration && <span>{lesson.duration}</span>}
                  {lesson.slides?.length > 0 && (
                    <span>{lesson.slides.length} slides</span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => { setEditingLesson(lesson); setIsNewLesson(false); }}
                  className="px-4 py-2 text-xs font-medium bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    console.log("Opening quiz for lesson:", lesson);
                    setQuizLesson(lesson);
                  }}
                  className="px-4 py-2 text-xs font-medium bg-teal-50 text-teal-700 hover:bg-teal-100 rounded-xl transition-colors"
                >
                  Quiz ({lesson.quiz?.questions?.length || 0})
                </button>
                <button
                  onClick={() => handleDeleteLesson(lesson._id || lesson.id)}
                  className="px-4 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-xl transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editingLesson && (
        <LessonFormModal
          lesson={editingLesson}
          isNew={isNewLesson}
          onSave={handleSaveLesson}
          onClose={() => {
            setEditingLesson(null);
            setIsNewLesson(false);
          }}
        />
      )}

      {quizLesson && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h3 className="font-bold text-xl">Lesson Quiz</h3>
                <p className="text-sm text-slate-500">{quizLesson.title}</p>
              </div>
              <button 
                onClick={() => setQuizLesson(null)} 
                className="text-2xl text-slate-400 hover:text-slate-600"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-auto p-6">
              <QuizEditor 
                quiz={quizLesson.quiz || { title: 'Lesson Quiz', questions: [] }}
                onChange={handleSaveLessonQuiz}
              />
            </div>

            <div className="p-6 border-t flex justify-end">
              <button
                onClick={() => setQuizLesson(null)}
                className="px-8 py-3 bg-teal-600 text-white font-semibold rounded-2xl hover:bg-teal-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}